/** ID of the injected <style> element, so injection stays idempotent. */
export const THREE_DS_CHALLENGE_STYLE_ID = "pnx-3ds-challenge-styles";

/**
 * The Nuvei Web SDK renders the 3DS challenge in its OWN modal: `inject3Dmodal`
 * appends a fixed, full-screen backdrop `<div>` (z-index 9999) to `document.body`
 * containing `.sfcModal-dialog > .sfcModal-content` with the ACS `<iframe id="ifrm">`
 * inside. There is no option to mount it into our own container, and the SDK ships
 * no styling for those classes.
 *
 * To make that modal look like our own dialog we override its structure here.
 * The styles MUST live in `document.head` (light DOM): the SDK's modal is appended
 * to `document.body`, outside the Qiankun shadow root that scopes the app's styles,
 * so CSS-module / shadow-root styles cannot reach it. `!important` is required where
 * the SDK sets the property inline (backdrop colour, content border).
 *
 * IMPORTANT: this style MUST be added with `insertAdjacentElement`, NOT `appendChild`.
 * When the app runs as a Qiankun MFE (staging/prod) Qiankun patches
 * `HTMLHeadElement.prototype.appendChild` to relocate dynamically-appended
 * `<style>`/`<link>` nodes into the MFE's shadow-root container (for scoping +
 * cleanup on unmount). That patch would put this style inside our shadow root —
 * never reaching the SDK modal in the light-DOM `document.body`. `insertAdjacentElement`
 * is not patched, so the style lands in the real `document.head`. (Locally the app runs
 * as a standalone SPA where `appendChild` is unpatched, which is why it worked in dev.)
 */
const CSS = `
/* Backdrop: the SDK's fixed full-screen div that directly wraps .sfcModal-dialog. */
body > div:has(> .sfcModal-dialog) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 16px !important;
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.sfcModal-dialog {
  min-width: 530px;
  max-width: 580px;
  margin: 0;
}

.sfcModal-content {
  border: none !important;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  overflow: hidden;
  padding: 30px;
}

.sfcModal-header {
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px 0;
}

.sfcModal-close {
  cursor: pointer;
}

/* The ACS challenge iframe.
   The SDK fixes this at height: 420px inline and never resizes it. The challenge
   is cross-origin, so we can't measure or react to its content (e.g. a taller
   result page after the user submits). Instead we give it a generous responsive
   height up front — capped on large screens — so both the challenge form and the
   post-submit response fit without being clipped. height needs !important to beat
   the SDK's inline value. */
#ifrm {
  width: 100% !important;
  min-width: 100% !important;
  height: 70vh !important;
  max-height: 600px !important;
}

/* Mobile: the desktop min-width (530px) would overflow a phone viewport, so make
   the challenge a full-screen sheet — dialog and iframe fill 100% width/height.
   Matches the app's 768px breakpoint (MOBILE_BREAKPOINT). */
@media (max-width: 768px) {
  body > div:has(> .sfcModal-dialog) {
    padding: 0 !important;
    align-items: stretch !important;
  }

  .sfcModal-dialog {
    min-width: 100% !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .sfcModal-content {
    height: 100% !important;
    border-radius: 0 !important;
  }

  #ifrm {
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
  }
}
`;

/**
 * Injects the 3DS challenge brand styles into `document.head` once. Safe to call
 * repeatedly (e.g. on every MFE mount) — it no-ops if already present.
 */
export const injectThreeDSChallengeStyles = (): void => {
  if (typeof document === "undefined") return;
  if (document.getElementById(THREE_DS_CHALLENGE_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = THREE_DS_CHALLENGE_STYLE_ID;
  style.textContent = CSS;
  document.head.insertAdjacentElement("beforeend", style);
};
